import type { Item } from '@itin/shared'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useT } from '../lib/i18n.js'

/**
 * One map per trip, assembled from tiles.
 *
 * Not a static-map image: MapTiler's Static Maps API is a paid endpoint, and
 * the same key that geocodes happily returns 403 for it. Raster tiles are free,
 * so the picture is assembled from those instead.
 *
 * Not a mapping library either. Leaflet or MapLibre would be ~150 KB to draw
 * something nobody pans, on a page whose whole premise is a bad connection. So:
 * a grid of `<img>` tiles with pins positioned over them. No JavaScript map, no
 * dependency, and it survives being printed. Tapping a pin is deliberately not
 * a feature — every entry below already carries an address that opens the
 * traveller's own maps app, which is what they will use to actually walk there.
 *
 * Tiles come through our own API, so the key never reaches the browser and
 * MapTiler never learns which of Ana's clients opened which itinerary.
 */

const TILE = 256
/** Close enough to read a street, far enough that a nine-day trip still fits. */
const MAX_ZOOM = 15
const MIN_ZOOM = 2
const HEIGHT = 420
/** Until the container has been measured. Any layout computed at this width is
 *  replaced on the first frame; it exists so the first render is not NaN. */
const ASSUMED_WIDTH = 640

/** Web Mercator, in tile units — the standard slippy-map projection. */
function project(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const n = 2 ** zoom
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat))
  const rad = (clamped * Math.PI) / 180
  return {
    x: ((lng + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n,
  }
}

/**
 * The closest zoom at which every pin still fits in the frame.
 *
 * Computed rather than fixed, because a trip may be four blocks of Paris or
 * three countries, and a zoom that suits one makes the other useless.
 */
function fitZoom(pins: Array<{ lat: number; lng: number }>, width: number): number {
  if (pins.length === 1) return 13
  for (let zoom = MAX_ZOOM; zoom > MIN_ZOOM; zoom--) {
    const points = pins.map((p) => project(p.lat, p.lng, zoom))
    const spanX = (Math.max(...points.map((p) => p.x)) - Math.min(...points.map((p) => p.x))) * TILE
    const spanY = (Math.max(...points.map((p) => p.y)) - Math.min(...points.map((p) => p.y))) * TILE
    // A tenth of margin, so a pin never sits on the frame's edge.
    if (spanX < width * 0.9 && spanY < HEIGHT * 0.9) return zoom
  }
  return MIN_ZOOM
}

export function TripMap({ token, items }: { token: string; items: Item[] }) {
  const t = useT()
  const [failed, setFailed] = useState(false)

  /**
   * The frame is whatever the column is, not a number chosen in advance.
   *
   * Hardcoding 960 laid the tiles out for a frame that did not exist: the
   * share page's column is 624px on a laptop and narrower on a phone, so the
   * map centred itself off-screen and half the tiles were clipped. Measuring
   * is the only version that is right at every width.
   */
  const frame = useRef<HTMLElement>(null)
  const [width, setWidth] = useState(ASSUMED_WIDTH)
  useEffect(() => {
    const el = frame.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.round(entry!.contentRect.width)
      if (next > 0) setWidth(next)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const layout = useMemo(() => {
    const pins = items
      .filter((i) => i.lat !== null && i.lng !== null)
      .map((i) => ({ lat: i.lat!, lng: i.lng!, title: i.title }))
    if (pins.length === 0) return null

    const zoom = fitZoom(pins, width)
    const points = pins.map((p) => ({ ...p, ...project(p.lat, p.lng, zoom) }))
    const centre = {
      x: (Math.min(...points.map((p) => p.x)) + Math.max(...points.map((p) => p.x))) / 2,
      y: (Math.min(...points.map((p) => p.y)) + Math.max(...points.map((p) => p.y))) / 2,
    }

    // Pixel of the frame's top-left corner, in this zoom's whole-world space.
    const originX = centre.x * TILE - width / 2
    const originY = centre.y * TILE - HEIGHT / 2

    const firstCol = Math.floor(originX / TILE)
    const lastCol = Math.floor((originX + width) / TILE)
    const firstRow = Math.floor(originY / TILE)
    const lastRow = Math.floor((originY + HEIGHT) / TILE)
    const span = 2 ** zoom

    const tiles: Array<{
      key: string
      z: number
      x: number
      y: number
      left: number
      top: number
    }> = []
    for (let row = firstRow; row <= lastRow; row++) {
      // Wrap east–west, skip north–south: there is no tile above the pole.
      if (row < 0 || row >= span) continue
      for (let col = firstCol; col <= lastCol; col++) {
        tiles.push({
          key: `${zoom}/${col}/${row}`,
          z: zoom,
          x: ((col % span) + span) % span,
          y: row,
          left: col * TILE - originX,
          top: row * TILE - originY,
        })
      }
    }

    return {
      tiles,
      pins: points.map((p) => ({
        title: p.title,
        left: p.x * TILE - originX,
        top: p.y * TILE - originY,
      })),
    }
  }, [items, width])

  // Nothing to show, or nothing that will show. A map that cannot load leaves no
  // trace: an empty grey rectangle reads as a broken page, and the itinerary
  // beneath it is the part that matters.
  if (!layout || failed) return null

  return (
    <figure
      ref={frame}
      data-testid="trip-map"
      aria-label={t('map.title')}
      className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
      style={{ height: HEIGHT }}
    >
      {layout.tiles.map((tile) => (
        <img
          key={tile.key}
          src={`/api/share/${encodeURIComponent(token)}/tiles/${tile.z}/${tile.x}/${tile.y}.png`}
          alt=""
          aria-hidden="true"
          width={TILE}
          height={TILE}
          loading="eager"
          // One bad tile is a grey square; no key or no provider means every
          // tile fails, and then the whole figure goes rather than leaving a
          // chequerboard of holes.
          onError={() => setFailed(true)}
          className="absolute max-w-none"
          style={{ left: tile.left, top: tile.top }}
        />
      ))}

      {layout.pins.map((pin, index) => (
        <span
          key={`${pin.left},${pin.top},${index}`}
          title={pin.title}
          className="absolute block size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-gold shadow"
          style={{ left: pin.left, top: pin.top }}
        />
      ))}
    </figure>
  )
}
